import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const emptyForm = {
  company: "",
  tax_number: "",
  salutation: "",
  first_name: "",
  last_name: "",
  street: "",
  house_number: "",
  zip_code: "",
  city: "",
  country: "Deutschland",
  email: "",
  phone: "",
};

export default function Profile() {
  const { user, loading, updateProfile } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm({
      company: user.company || "",
      tax_number: user.tax_number || "",
      salutation: user.salutation || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      street: user.street || "",
      house_number: user.house_number || "",
      zip_code: user.zip_code || "",
      city: user.city || "",
      country: user.country || "Deutschland",
      email: user.email || "",
      phone: user.phone || "",
    });
  }, [user]);

  if (loading) return <div className="page-wrap py-20 text-center text-gray-500">Lade…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  const isDealer = user.role === "dealer";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await updateProfile(form);
      toast.success("Profil gespeichert.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Profil konnte nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrap py-16 max-w-2xl">
      <div className="swiss-label text-[#16A34A]">Mein Profil</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-2">
        {isDealer ? "Händlerprofil bearbeiten" : "Privatprofil bearbeiten"}
      </h1>
      <p className="text-gray-500 mt-2 text-sm">Aktualisiere deine Kontakt- und Adressdaten.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        {isDealer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Firma *">
              <input required value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="input" />
            </Field>
            <Field label="Steuernummer *">
              <input required value={form.tax_number} onChange={(e) => setForm({...form, tax_number: e.target.value})} className="input" />
            </Field>
          </div>
        )}

        <Field label="Anrede *">
          <select required value={form.salutation} onChange={(e) => setForm({...form, salutation: e.target.value})} className="input bg-white">
            <option value="">Bitte wählen</option>
            <option value="Herr">Herr</option>
            <option value="Frau">Frau</option>
            <option value="Divers">Divers</option>
          </select>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Vorname *">
            <input required value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="input" />
          </Field>
          <Field label="Nachname *">
            <input required value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-4">
          <Field label="Straße *">
            <input required value={form.street} onChange={(e) => setForm({...form, street: e.target.value})} className="input" />
          </Field>
          <Field label="Nummer *">
            <input required value={form.house_number} onChange={(e) => setForm({...form, house_number: e.target.value})} className="input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4">
          <Field label="PLZ *">
            <input required value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})} className="input" />
          </Field>
          <Field label="Ort *">
            <input required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="input" />
          </Field>
        </div>

        <Field label="Land *">
          <input required value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} className="input" />
        </Field>
        <Field label="E-Mail *">
          <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input" />
        </Field>
        <Field label="Telefon">
          <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input" />
        </Field>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3">{error}</div>}
        <button disabled={saving} className="swiss-btn-primary w-full">{saving ? "Speichern…" : "Profil speichern"}</button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="swiss-label">{label}</label>
      {React.cloneElement(children, {
        className: `${children.props.className || ""} w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]`,
      })}
    </div>
  );
}
